import React, { useState } from 'react';
import { Button, Dialog, DialogContent, DialogTitle } from '@onward-delivery/design';
import { CardRow, StatusTag, SystemCard } from './blocks';
import { lastSyncOf, tmsMethodOf, typeLabelOf } from './utils';

/**
 * One shipper's integrations with the carrier, read-only: they're set up outside this page.
 * @param {{ row: ReturnType<typeof import('./utils').shipperIntegrationRowsFor>[number], onClose: () => void }} props
 */
const ShipperIntegrationsDialog = ({ row, onClose }) => (
    <Dialog
        open
        onOpenChange={(next) => {
            if (!next) onClose();
        }}
    >
        <DialogContent aria-describedby={undefined} className="w-[560px] max-w-[95vw] rounded-2xl p-0">
            <div className="border-b border-border px-6 pb-4 pt-5">
                {/* mr-6 keeps clear of DialogContent's own close button. */}
                <DialogTitle className="mr-6 text-xl font-bold text-foreground">
                    Integrations — {row.shipper.business_name}
                </DialogTitle>
            </div>
            <div className="flex flex-col gap-4 px-6 py-5">
                {row.integrations.map((integration) => (
                    <div key={integration.override_id} className="overflow-hidden rounded-lg border border-slate-200">
                        <CardRow label="Type">{typeLabelOf(integration.type)}</CardRow>
                        <CardRow label="Status">
                            <StatusTag status={integration.status} />
                        </CardRow>
                        <CardRow label="Method">{tmsMethodOf(integration)}</CardRow>
                        <CardRow label="Last sync">{lastSyncOf(integration.last_success_at)}</CardRow>
                    </div>
                ))}
            </div>
        </DialogContent>
    </Dialog>
);

const ShipperRow = ({ row, onView }) => (
    <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-4 py-3 text-[13px]">
        <div className="flex min-w-0 flex-col">
            <span className="truncate font-semibold text-slate-900">{row.shipper.business_name}</span>
            <span className="truncate text-slate-500">
                {row.types.map(typeLabelOf).join(', ')} · Last sync {lastSyncOf(row.lastSuccessAt)}
            </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
            <StatusTag status={row.status} />
            <Button variant="outline" size="sm" onClick={() => onView(row)}>
                View
            </Button>
        </div>
    </div>
);

/**
 * The carrier's integrations with the org's shippers, one row per shipper that has its own. View shows them read-only.
 * @param {{ rows: ReturnType<typeof import('./utils').shipperIntegrationRowsFor>, loading?: boolean }} props `loading`
 *   while the org shippers' integrations are still loading
 */
const ShipperIntegrationsCard = ({ rows, loading = false }) => {
    const [viewing, setViewing] = useState(null);

    let content = <span className="px-4 py-3 text-[13px] text-slate-500">No shipper integrations yet.</span>;
    if (loading) content = <span className="px-4 py-3 text-[13px] text-slate-500">Loading…</span>;
    else if (rows.length)
        content = rows.map((row) => <ShipperRow key={row.shipper.client_id} row={row} onView={setViewing} />);

    return (
        <SystemCard
            badge="S"
            badgeClassName="bg-indigo-700"
            title="Shipper integrations"
            subtitle="set up with one of the org's shippers at a time"
        >
            {content}
            {viewing ? <ShipperIntegrationsDialog row={viewing} onClose={() => setViewing(null)} /> : null}
        </SystemCard>
    );
};

export default ShipperIntegrationsCard;
